import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Gamepad2, Play, Square, Loader2, Radio, Layers, Wand2, Sparkles, Cpu, Save, Trash2, Share2, GripVertical, Search, Star, Mic, GitCompare, Clapperboard, Zap } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { audioUrl, formatApiError, API } from "@/lib/api";
import { createWebMixBus } from "@/lib/mixBusWeb";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ThemeMixer from "@/components/ThemeMixer";
import PulsePanel from "@/components/PulsePanel";
import ModelPreferenceBadge, { AiSourceTag, recordAiPreference } from "@/components/ModelPreference";

const STATES = [
  { key: "exploration", label: "Exploration", color: "#00F0FF" },
  { key: "tension", label: "Tension", color: "#8A2BE2" },
  { key: "combat", label: "Combat", color: "#FF9500" },
  { key: "boss", label: "Boss", color: "#FF00AA" },
];
const MOODS = ["cinematic", "dark", "heroic", "mysterious", "aggressive"];
const ENVIRONMENTS = [["", "Aucun (sec)"], ["room", "Petite pièce"], ["chamber", "Chambre"], ["hall", "Salle de concert"], ["cathedral", "Cathédrale"], ["cave", "Caverne"], ["forest", "Forêt"], ["plate", "Plate"], ["arena", "Arène"], ["space", "Espace"]];
const GENRES = ["Electronic", "Orchestral", "Synthwave", "Ambient", "Industrial"];
const SOUNDSCAPE_PRESETS = [
  { name: "Dungeon", mood: "mysterious", genre: "Ambient" },
  { name: "Chase", mood: "aggressive", genre: "Synthwave" },
  { name: "Victory", mood: "heroic", genre: "Orchestral" },
  { name: "Menu", mood: "cinematic", genre: "Electronic" },
  { name: "Boss", mood: "dark", genre: "Industrial" },
];
const ALL_LAYERS = ["ambient_pad", "bass", "drums", "lead_synth", "brass_hits"];
const SOUNDSCAPE_LAYER_KEYS = { ambient_pad: 1, bass: 1, drums: 1, lead_synth: 1 };

const MOOD_SYNONYMS = {
  dark: ["dark", "ominous", "oppressive", "brutal", "grim", "sinister", "menacing", "apocalyptic", "horror", "dread"],
  aggressive: ["aggressive", "intense", "relentless", "violent", "frantic", "combat", "furious", "driving"],
  mysterious: ["mysterious", "eerie", "stealth", "tense", "suspense", "enigmatic", "uneasy", "unsettling"],
  heroic: ["heroic", "triumphant", "epic", "victorious", "uplifting", "majestic", "brave"],
  cinematic: ["cinematic", "dramatic", "emotional", "atmospheric", "calm", "melancholic"],
};
const matchMood = (raw) => {
  const s = (raw || "").toLowerCase();
  return MOODS.find((m) => s.includes(m)) || Object.keys(MOOD_SYNONYMS).find((m) => MOOD_SYNONYMS[m].some((w) => s.includes(w))) || MOODS[0];
};

export default function Gaming() {
  const { refreshUser } = useAuth();
  const [session, setSession] = useState(null);
  const [params, setParams] = useState(null);
  const [intensity, setIntensity] = useState(0.2);
  const [mood, setMood] = useState("cinematic");
  const [activeState, setActiveState] = useState("exploration");
  const [playing, setPlaying] = useState(false);
  const [engineMode, setEngineMode] = useState("synth");
  const [scGenre, setScGenre] = useState("Electronic");
  const [soundscape, setSoundscape] = useState(null);
  const [scLoading, setScLoading] = useState(false);
  const [scEngine, setScEngine] = useState("fal");
  const [aiScene, setAiScene] = useState("");
  const [aiSceneLoading, setAiSceneLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState("openai");
  const [scCompare, setScCompare] = useState(null);
  const [scCompareLoading, setScCompareLoading] = useState(false);
  const [scSource, setScSource] = useState(null);
  const cutsceneAudio = useRef(null);

  // Cutscene cue: fetch the merged scene (dialogue + music) and play it over the adaptive engine.
  const playCutscene = async (p) => {
    try {
      if (cutsceneAudio.current) { cutsceneAudio.current.pause(); cutsceneAudio.current = null; }
      const cue = p.cue || {};
      const qs = `pause=${cue.pause ?? 0.6}${cue.bg ? `&bg=${cue.bg}&bg_gain=${cue.bg_gain ?? 0.18}` : ""}`;
      const res = await fetch(`${API}/voice/dialogue/${cue.dialogue_id}/export?${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error("scene unavailable");
      const url = URL.createObjectURL(await res.blob());
      const a = new Audio(url);
      cutsceneAudio.current = a;
      a.onended = () => URL.revokeObjectURL(url);
      await a.play();
      toast.success(`Cutscene « ${p.name.replace("Cutscene · ", "")} » — mood ${p.mood} déclenché.`);
    } catch {
      toast.error("Impossible de lire la scène de la cutscene.");
    }
  };
  const [userPresets, setUserPresets] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetProject, setPresetProject] = useState("none");
  const [presetQuery, setPresetQuery] = useState("");
  const [myProjects, setMyProjects] = useState([]);
  const engine = useRef(null);
  const debounce = useRef(null);
  const scPoll = useRef(null);

  const fetchPresets = useCallback(() => {
    api.get("/adaptive/presets").then(({ data }) => setUserPresets(data)).catch(() => {});
  }, []);

  const dragIdx = useRef(null);
  const persistOrder = (list) => {
    setUserPresets(list);
    api.post("/adaptive/presets/reorder", { order: list.map((p) => p.id) }).catch(() => {});
  };
  const onPresetDrop = (targetId) => {
    const fromId = dragIdx.current;
    dragIdx.current = null;
    if (presetQuery.trim()) return;
    if (!fromId || fromId === targetId) return;
    const list = [...userPresets];
    const from = list.findIndex((p) => p.id === fromId);
    const to = list.findIndex((p) => p.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    persistOrder(list);
  };

  useEffect(() => {
    api.post("/adaptive/session", { game_name: "Demo Game", mood: "cinematic" })
      .then(({ data }) => { setSession(data); setParams(data.params); })
      .catch(() => toast.error("Could not start adaptive session."));
    // reuse a previous completed soundscape if available
    api.get("/adaptive/soundscapes").then(({ data }) => {
      const ready = data.find((s) => s.status === "completed" && Object.keys(s.layers || {}).length);
      if (ready) setSoundscape(ready);
    }).catch(() => {});
    fetchPresets();
    api.get("/projects").then(({ data }) => setMyProjects(data)).catch(() => {});
    return () => { stopEngine(); clearTimeout(scPoll.current); };
  }, []);

  const applyParams = useCallback((p) => {
    setParams(p);
    setIntensity(p.intensity);
    if (engine.current) engine.current.update(p);
  }, []);

  const pushIntensity = (val) => {
    setIntensity(val);
    const p = { ...params, intensity: val };
    if (engine.current) engine.current.update(p);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      if (!session) return;
      const { data } = await api.post("/adaptive/intensity", { session_id: session.session_id, intensity: val });
      setParams(data.params);
    }, 250);
  };

  const transition = async (state) => {
    if (!session) return;
    setActiveState(state);
    const { data } = await api.post("/adaptive/transition", { session_id: session.session_id, state });
    applyParams(data.params);
  };

  const applySuggestion = (data, provider) => {
    const m = matchMood(data.mood);
    changeMood(m);
    const g = (data.genre || "").toLowerCase();
    const matchedGenre = GENRES.find((x) => x.toLowerCase() === g) || GENRES.find((x) => g.includes(x.toLowerCase()) || x.toLowerCase().includes(g));
    if (matchedGenre) setScGenre(matchedGenre);
    if (provider) { setScSource(provider); recordAiPreference(provider, "soundscape"); }
    return { m, matchedGenre };
  };

  const suggestFromScene = async () => {
    if (!aiScene.trim()) { toast.error("Describe a game scene first."); return; }
    setAiSceneLoading(true);
    try {
      const { data } = await api.post("/ai/suggest-soundscape", { scene: aiScene.trim(), provider: aiProvider });
      const { m, matchedGenre } = applySuggestion(data, aiProvider);
      const layerNames = (data.layers || []).map((l) => l.name).filter(Boolean).join(", ");
      toast.success(`AI suggestion: ${m} · ${matchedGenre || data.genre || ""}${layerNames ? ` — ${layerNames}` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI suggestion failed.");
    } finally { setAiSceneLoading(false); }
  };

  const compareScene = async () => {
    if (!aiScene.trim()) { toast.error("Describe a game scene first."); return; }
    setScCompareLoading(true);
    setScCompare(null);
    try {
      const [o, cl] = await Promise.all([
        api.post("/ai/suggest-soundscape", { scene: aiScene.trim(), provider: "openai" }),
        api.post("/ai/suggest-soundscape", { scene: aiScene.trim(), provider: "claude" }),
      ]);
      setScCompare({ openai: o.data, claude: cl.data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI comparison failed.");
    } finally { setScCompareLoading(false); }
  };

  const [environment, setEnvironment] = useState("");
  const changeEnvironment = async (env) => {
    setEnvironment(env);
    if (!session) return;
    try {
      const { data } = await api.post("/adaptive/environment", { session_id: session.session_id, environment: env || null });
      applyParams(data.params);
      toast.success(env ? `AmbientCrafter : ${ENVIRONMENTS.find(([k]) => k === env)?.[1]} — réverb ${Math.round((data.params.mix?.reverb?.mix || 0) * 100)} % (moins en combat).` : "Environnement retiré (son sec).");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Environnement impossible."); }
  };

  const changeMood = async (m) => {
    setMood(m);
    if (!session) return;
    const { data } = await api.post("/adaptive/mood", { session_id: session.session_id, mood: m });
    applyParams(data.params);
  };

  const generateSoundscape = async (moodArg = mood, genreArg = scGenre) => {
    setScLoading(true);
    try {
      const { data } = await api.post("/adaptive/soundscape", { mood: moodArg, genre: genreArg, engine: scEngine });
      await refreshUser();
      toast.success(scEngine === "surge" ? "Rendu Surge XT des 4 couches — quelques secondes." : "Rendering 4 AI layers — this takes ~2-3 min.");
      const poll = async () => {
        try {
          const { data: sc } = await api.get(`/adaptive/soundscape/${data.id}`);
          if (sc.status === "completed") { setSoundscape(sc); setScLoading(false); toast.success("Soundscape ready!"); return; }
          if (sc.status === "failed") { setScLoading(false); toast.error("Soundscape generation failed."); return; }
        } catch { /* keep polling */ }
        scPoll.current = setTimeout(poll, 4000);
      };
      scPoll.current = setTimeout(poll, 4000);
    } catch (e) {
      setScLoading(false);
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const savePreset = async () => {
    if (!presetName.trim()) { toast.error("Name your preset."); return; }
    try {
      await api.post("/adaptive/presets", { name: presetName.trim(), mood, genre: scGenre, project_id: presetProject === "none" ? null : presetProject });
      setPresetName("");
      setPresetProject("none");
      setSaveOpen(false);
      fetchPresets();
      toast.success("Preset saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deletePreset = async (id) => {
    try { await api.delete(`/adaptive/presets/${id}`); fetchPresets(); toast.success("Preset removed."); }
    catch { toast.error("Could not remove preset."); }
  };

  const togglePin = async (id) => {
    try { await api.post(`/adaptive/presets/${id}/pin`); fetchPresets(); }
    catch { toast.error("Could not update favourite."); }
  };

  const startEngine = async () => {
    if (engineMode === "layers") {
      if (!soundscape || !Object.keys(soundscape.layers || {}).length) {
        toast.error("Generate an AI soundscape first.");
        return;
      }
      engine.current = createLayeredEngine(soundscape.layers);
    } else {
      engine.current = createSynthEngine();
    }
    try {
      await engine.current.start();
      engine.current.update({ ...params, intensity });
      setPlaying(true);
    } catch {
      toast.error("Could not start audio engine.");
      engine.current = null;
    }
  };
  const stopEngine = () => {
    if (engine.current) { engine.current.stop(); engine.current = null; }
    setPlaying(false);
  };

  const switchMode = (mode) => {
    if (playing) stopEngine();
    setEngineMode(mode);
  };

  if (!session) {
    return <div className="p-8 flex items-center gap-3 text-muted-foreground"><Loader2 className="animate-spin text-primary" /> Starting adaptive engine…</div>;
  }

  const layersReady = soundscape && Object.keys(soundscape.layers || {}).length > 0;

  return (
    <div className="p-6 lg:p-8 max-w-6xl overflow-x-hidden">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
            <Gamepad2 className="text-secondary" /> Adaptive Gaming Music
          </h1>
          <p className="text-muted-foreground mt-1">Drive the score in real time from your game state — like an in-engine SDK.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/dashboard/voice"
            className="inline-flex items-center gap-2 rounded-md border border-secondary/40 text-secondary hover:bg-secondary/10 h-10 px-4 text-sm font-medium transition-colors"
            data-testid="quick-voice-gaming"
          >
            <Mic size={15} /> Voix / SFX
          </Link>
          <Button
            onClick={playing ? stopEngine : startEngine}
            className={playing ? "bg-destructive text-white hover:bg-destructive/90" : "bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"}
            data-testid="engine-toggle"
          >
            {playing ? <><Square size={16} className="mr-1.5" /> Stop Engine</> : <><Play size={16} className="mr-1.5" /> Play Live Engine</>}
          </Button>
        </div>
      </header>
      <div className="mb-8"><ThemeMixer /></div>
      <div className="mb-8"><PulsePanel session={session} onParams={(p, m) => { applyParams(p); if (m) setMood(m); }} /></div>

      {/* Engine mode selector */}
      <div className="rounded-2xl border border-border bg-card p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <ModeButton active={engineMode === "synth"} onClick={() => switchMode("synth")} icon={Cpu} title="Synth Engine" desc="Instant Web Audio synthesis" testId="mode-synth" />
        <ModeButton active={engineMode === "layers"} onClick={() => switchMode("layers")} icon={Layers} title="AI Layers" desc={layersReady ? "Real generated stem layers" : "Generate real audio layers"} testId="mode-layers" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6 min-w-0">
          {engineMode === "layers" && (
            <div className="rounded-2xl border border-secondary/30 bg-secondary/[0.04] p-6" data-testid="soundscape-panel">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="text-secondary" size={18} /><h3 className="font-display text-lg font-bold">AI Soundscape</h3>
                {layersReady && <Badge className="bg-primary/15 text-primary border-primary/30">Ready · {Object.keys(soundscape.layers).length} layers</Badge>}
              </div>
              <p className="text-sm text-muted-foreground mb-4">Generate 4 loopable stem layers (pad, bass, drums, lead) with AI. The engine crossfades them live as intensity changes.</p>
              {soundscape?.patches && (
                <div className="mb-3 text-[11px] font-mono text-amber-400/90 flex flex-wrap gap-x-3 gap-y-1" data-testid="soundscape-patches">
                  <span className="inline-flex items-center gap-1"><Zap size={11} /> Surge XT · {soundscape.bpm} BPM · {soundscape.key}</span>
                  {Object.entries(soundscape.patches).map(([l, p]) => <span key={l}>{l.replace("_", " ")}: <span className="text-foreground/80">{p}</span></span>)}
                </div>
              )}
              <ModelPreferenceBadge className="mb-3" />

              <div className="mb-4 rounded-xl border border-primary/25 bg-primary/[0.04] p-3">
                <div className="text-xs font-mono text-primary mb-2 flex items-center gap-1.5 flex-wrap"><Sparkles size={12} /> AI SCENE → SOUNDSCAPE
                  {scSource && <AiSourceTag provider={scSource} prefix="suggéré par" testId="soundscape-ai-label" />}
                  <button type="button" onClick={() => setAiProvider((p) => (p === "openai" ? "claude" : "openai"))} className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 normal-case" data-testid="soundscape-provider-toggle" title="Switch AI model">
                    {aiProvider === "openai" ? "ChatGPT" : "Claude"}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={aiScene}
                    onChange={(e) => setAiScene(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && suggestFromScene()}
                    placeholder="e.g. sneaking through an abandoned space station"
                    className="flex-1 min-w-[180px] w-full sm:w-auto bg-background border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="ai-scene-input"
                  />
                  <Button onClick={suggestFromScene} disabled={aiSceneLoading} size="sm" className="h-9 bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5 shrink-0" data-testid="ai-scene-suggest">
                    {aiSceneLoading ? <Loader2 className="animate-spin" size={14} /> : <Wand2 size={14} />} Suggest
                  </Button>
                  <Button onClick={compareScene} disabled={scCompareLoading} size="sm" variant="ghost" className="h-9 text-secondary hover:text-secondary hover:bg-secondary/10 gap-1.5 shrink-0" data-testid="soundscape-compare">
                    {scCompareLoading ? <Loader2 className="animate-spin" size={14} /> : <GitCompare size={14} />} Comparer
                  </Button>
                </div>
                {scCompare && (
                  <div className="grid sm:grid-cols-2 gap-2 mt-3" data-testid="soundscape-compare-results">
                    {[["openai", "ChatGPT"], ["claude", "Claude"]].map(([key, label]) => {
                      const d = scCompare[key];
                      return (
                        <div key={key} className="rounded-lg border border-secondary/25 bg-secondary/[0.04] p-2.5 flex flex-col" data-testid={`soundscape-compare-${key}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-mono text-secondary">{label}</span>
                            <span className="text-[10px] font-mono text-muted-foreground capitalize">{d.mood} · {d.genre}</span>
                          </div>
                          {d.description && <p className="text-xs text-foreground/90 leading-relaxed">{d.description}</p>}
                          <div className="flex flex-wrap gap-1 mt-1.5 flex-1">
                            {(d.layers || []).map((l) => (
                              <span key={l.name} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">{l.name}{typeof l.intensity === "number" ? ` ${Math.round(l.intensity * 100)}%` : ""}</span>
                            ))}
                          </div>
                          <Button onClick={() => { const { m, matchedGenre } = applySuggestion(d, key); toast.success(`Suggestion ${label} appliquée ✨ — ${m} · ${matchedGenre || d.genre}`); }} size="sm" className="mt-2 h-7 bg-secondary text-white hover:bg-secondary/90 self-start text-xs" data-testid={`soundscape-compare-${key}-use`}>
                            Utiliser
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="text-xs font-mono text-muted-foreground mb-2">ONE-TAP PRESETS</div>
                <div className="flex flex-wrap gap-2">
                  {SOUNDSCAPE_PRESETS.map((p) => (
                    <button
                      key={p.name}
                      disabled={scLoading}
                      onClick={() => { setScGenre(p.genre); changeMood(p.mood); generateSoundscape(p.mood, p.genre); }}
                      data-testid={`soundscape-preset-${p.name.toLowerCase()}`}
                      className="text-xs font-medium px-3 py-1.5 rounded-full border border-secondary/40 text-secondary hover:bg-secondary/15 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Sparkles size={11} /> {p.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono text-muted-foreground">MY PRESETS</div>
                  <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 text-muted-foreground hover:text-primary gap-1" data-testid="save-preset-open"><Save size={13} /> Save current</Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-border max-w-sm">
                      <DialogHeader><DialogTitle className="font-display">Save soundscape preset</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Saves the current mood (<span className="text-foreground capitalize">{mood}</span>) and genre (<span className="text-foreground">{scGenre}</span>).</p>
                        <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="e.g. Space Station" className="bg-background border-border" data-testid="preset-name-input" />
                        <div className="space-y-1.5">
                          <span className="text-xs text-muted-foreground">Share with project (optional)</span>
                          <Select value={presetProject} onValueChange={setPresetProject}>
                            <SelectTrigger className="bg-background border-border" data-testid="preset-project-select"><SelectValue placeholder="Keep private" /></SelectTrigger>
                            <SelectContent className="glass border-border max-h-52">
                              <SelectItem value="none">Keep private</SelectItem>
                              {myProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={savePreset} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-preset-submit">Save Preset</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                {userPresets.length === 0 ? (
                  <div className="text-xs text-muted-foreground/70">No saved presets yet — tune mood & genre, then Save current.</div>
                ) : (
                  <>
                    {userPresets.length > 4 && (
                      <div className="relative mb-2">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={presetQuery}
                          onChange={(e) => setPresetQuery(e.target.value)}
                          placeholder="Search presets…"
                          className="bg-background border-border h-8 pl-8 text-xs"
                          data-testid="preset-search"
                        />
                      </div>
                    )}
                    {(() => {
                      const q = presetQuery.trim().toLowerCase();
                      const filtered = q ? userPresets.filter((p) => p.name.toLowerCase().includes(q)) : userPresets;
                      if (filtered.length === 0) return <div className="text-xs text-muted-foreground/70" data-testid="preset-no-match">No presets match &quot;{presetQuery}&quot;.</div>;
                      return (
                        <div className="flex flex-wrap gap-2" data-testid="preset-list">
                          {filtered.map((p) => (
                            <div
                              key={p.id}
                              draggable={!q}
                              onDragStart={() => { dragIdx.current = p.id; }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => onPresetDrop(p.id)}
                              className={`group flex items-center rounded-full border border-primary/40 overflow-hidden ${q ? "" : "cursor-grab active:cursor-grabbing"}`}
                              data-testid={`user-preset-${p.id}`}
                            >
                              {!q && <span className="pl-2 text-muted-foreground/50 group-hover:text-muted-foreground" title="Drag to reorder"><GripVertical size={12} /></span>}
                              <button onClick={() => togglePin(p.id)} className={`pl-2 ${p.pinned ? "text-amber-400" : "text-muted-foreground/50 hover:text-amber-400"} transition-colors`} data-testid={`pin-preset-${p.id}`} title={p.pinned ? "Unpin" : "Pin to top"}>
                                <Star size={12} fill={p.pinned ? "currentColor" : "none"} />
                              </button>
                              <button
                                disabled={scLoading}
                                onClick={() => { setScGenre(p.genre); changeMood(p.mood); generateSoundscape(p.mood, p.genre); if (p.kind === "cutscene") playCutscene(p); }}
                                className={`text-xs font-medium ${q ? "pl-3" : "pl-1.5"} pr-2 py-1.5 ${p.kind === "cutscene" ? "text-secondary hover:bg-secondary/15" : "text-primary hover:bg-primary/15"} transition-colors disabled:opacity-50 flex items-center gap-1.5`}
                                data-testid={`play-preset-${p.id}`}
                                title={p.kind === "cutscene" ? `Cutscene cue — ${p.cue?.lines || 0} lines, plays the scene and switches mood to ${p.mood}` : p.shared ? `Shared in ${p.project_name || "project"}` : "Personal preset"}
                              >
                                {p.kind === "cutscene" && <Clapperboard size={12} data-testid={`cutscene-icon-${p.id}`} />}
                                {p.shared ? <Share2 size={11} /> : <Sparkles size={11} />} {p.name}
                                {p.shared && !p.owned && p.creator_name && <span className="text-[9px] text-muted-foreground ml-1">· by {p.creator_name}</span>}
                              </button>
                              {p.owned && (
                                <button onClick={() => deletePreset(p.id)} className="px-2 py-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" data-testid={`delete-preset-${p.id}`} title="Delete preset">
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <span className="text-xs text-muted-foreground">Genre</span>
                  <Select value={scGenre} onValueChange={setScGenre}>
                    <SelectTrigger className="bg-background border-border w-40" data-testid="soundscape-genre-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass border-border">{GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <span className="text-xs text-muted-foreground">Moteur</span>
                  <div className="flex rounded-md border border-border overflow-hidden h-10" data-testid="soundscape-engine">
                    <button type="button" onClick={() => setScEngine("fal")} className={`px-3 text-xs font-medium ${scEngine === "fal" ? "bg-secondary text-white" : "bg-background text-muted-foreground hover:text-foreground"}`} data-testid="soundscape-engine-fal">IA fal.ai · 6 cr</button>
                    <button type="button" onClick={() => setScEngine("surge")} className={`px-3 text-xs font-medium inline-flex items-center gap-1 ${scEngine === "surge" ? "bg-amber-500 text-black" : "bg-background text-muted-foreground hover:text-foreground"}`} data-testid="soundscape-engine-surge"><Zap size={11} /> Surge XT · 1 cr</button>
                  </div>
                </div>
                <Button onClick={() => generateSoundscape()} disabled={scLoading} className={scEngine === "surge" ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-secondary text-white hover:bg-secondary/90"} data-testid="generate-soundscape">
                  {scLoading ? <><Loader2 className="animate-spin mr-1.5" size={15} /> Rendering…</> : <><Wand2 size={15} className="mr-1.5" /> Generate Soundscape · {scEngine === "surge" ? "1" : "6"} cr</>}
                </Button>
              </div>
            </div>
          )}

          {/* Game states */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold mb-4">Game State</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {STATES.map((s) => (
                <button key={s.key} onClick={() => transition(s.key)} data-testid={`state-${s.key}`}
                  className={`rounded-xl border p-4 text-center transition-all ${activeState === s.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 bg-background"}`}>
                  <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ background: s.color, boxShadow: `0 0 12px ${s.color}` }} />
                  <div className="text-sm font-medium">{s.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Intensity */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">Intensity</h3>
              <span className="font-mono text-2xl font-bold text-primary">{Math.round(intensity * 100)}%</span>
            </div>
            <Slider value={[intensity]} min={0} max={1} step={0.01} onValueChange={(v) => pushIntensity(v[0])} data-testid="intensity-slider" />
            <div className="flex justify-between text-[11px] font-mono text-muted-foreground mt-2"><span>CALM</span><span>INTENSE</span></div>
          </div>

          {/* Mood */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold mb-3">Mood</h3>
            <Select value={mood} onValueChange={changeMood}>
              <SelectTrigger className="bg-background border-border w-full sm:w-64" data-testid="adaptive-mood-select"><SelectValue /></SelectTrigger>
              <SelectContent className="glass border-border">{MOODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Environment (AmbientCrafter → Mix Bus live) */}
          <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/[0.04] p-6" data-testid="adaptive-environment-card">
            <h3 className="font-display text-lg font-bold mb-1">Environnement</h3>
            <p className="text-[11px] text-muted-foreground mb-3">AmbientCrafter → réverb / ducking / limiteur envoyés aux SDK (<code>params.mix</code>, <code>SetEnvironment()</code>).</p>
            <Select value={environment || "none"} onValueChange={(v) => changeEnvironment(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-background border-border w-full sm:w-64" data-testid="adaptive-environment-select"><SelectValue placeholder="Aucun (sec)" /></SelectTrigger>
              <SelectContent className="glass border-border">{ENVIRONMENTS.map(([k, l]) => <SelectItem key={k || "none"} value={k || "none"}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {params?.mix && (
              <div className="grid grid-cols-3 gap-2 mt-4" data-testid="adaptive-mix-readout">
                <Stat label="Réverb" value={params.mix.reverb ? `${params.mix.reverb.space} · ${Math.round(params.mix.reverb.mix * 100)}%` : "—"} />
                <Stat label="Ducking" value={params.mix.ducking?.active ? `${Math.round(params.mix.ducking.level * 100)}%` : "off"} />
                <Stat label="Limiteur" value={`${params.mix.limiter?.ceiling_db} dBTP`} />
              </div>
            )}
          </div>
        </div>

        {/* Engine readout */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Radio size={16} className={playing ? "text-primary animate-pulse" : "text-muted-foreground"} />
              <h3 className="font-display text-lg font-bold">Engine Output</h3>
              <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground uppercase">{engineMode}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <Stat label="BPM" value={params?.bpm} />
              <Stat label="Master Vol" value={`${Math.round((params?.master_volume || 0) * 100)}%`} />
            </div>
            <div className="space-y-2">
              {(engineMode === "layers" ? Object.keys(SOUNDSCAPE_LAYER_KEYS) : ALL_LAYERS).map((layer) => {
                const active = params?.layers?.includes(layer);
                return (
                  <div key={layer} className="flex items-center gap-3">
                    <span className={`text-xs font-mono w-28 ${active ? "text-foreground" : "text-muted-foreground/40"}`}>{layer}</span>
                    <div className="flex-1 h-6 flex items-center gap-[2px]">
                      {Array.from({ length: 20 }).map((_, i) => (
                        <span key={i} className="flex-1 rounded-sm" style={{
                          height: active ? "100%" : "20%",
                          background: active ? "hsl(var(--primary))" : "hsl(var(--muted))",
                          animation: active && playing ? `eq ${0.5 + (i % 4) * 0.1}s ease-in-out ${i * 0.02}s infinite` : "none",
                          opacity: active ? 1 : 0.4,
                        }} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 font-mono text-xs">
            <div className="text-muted-foreground mb-2">// SDK usage</div>
            <pre className="text-primary whitespace-pre-wrap leading-relaxed">{`music.setIntensity(${intensity.toFixed(2)});
music.setMood("${mood}");
music.transition("${activeState}");`}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl bg-background border border-border p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-mono text-xl font-bold">{value ?? "—"}</div>
  </div>
);

const ModeButton = ({ active, onClick, icon: Icon, title, desc, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex-1 flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${active ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40"}`}>
    <Icon size={20} className={active ? "text-primary" : "text-muted-foreground"} />
    <div>
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
  </button>
);

// ---------- Real AI layer engine: crossfades generated stem loops ----------
function createLayeredEngine(layers) {
  let ctx, master, mixBus;
  const nodes = {};
  const els = [];

  const start = async () => {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") await ctx.resume();
    master = ctx.createGain();
    master.gain.value = 0.9;
    mixBus = createWebMixBus(ctx, master);
    const loads = [];
    for (const [layer, path] of Object.entries(layers)) {
      const el = new Audio(audioUrl(path));
      el.loop = true;
      el.crossOrigin = "use-credentials";
      els.push(el);
      const src = ctx.createMediaElementSource(el);
      const g = ctx.createGain();
      g.gain.value = 0;
      src.connect(g);
      g.connect(master);
      nodes[layer] = g;
      loads.push(el.play().catch(() => {}));
    }
    await Promise.all(loads);
  };

  const update = (p) => {
    if (!ctx || !p) return;
    const t = ctx.currentTime;
    master.gain.linearRampToValueAtTime(p.master_volume ?? 0.8, t + 0.5);
    mixBus?.update(p.mix);
    for (const [layer, g] of Object.entries(nodes)) {
      const on = p.layers?.includes(layer);
      g.gain.linearRampToValueAtTime(on ? 0.9 : 0.0, t + 0.6);
    }
  };

  const stop = () => {
    els.forEach((e) => { try { e.pause(); } catch { /* noop */ } });
    if (ctx) { try { setTimeout(() => ctx.close(), 200); } catch { /* noop */ } }
  };

  return { start, update, stop };
}

// ---------- Instant synth engine (Web Audio oscillators) ----------
function createSynthEngine() {
  let ctx, master, mixBus, padOsc, padOsc2, bassOsc, filter, padGain, bassGain, drumGain, drumInterval;

  const start = async () => {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") await ctx.resume();
    master = ctx.createGain(); master.gain.value = 0; mixBus = createWebMixBus(ctx, master);
    filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 600; filter.connect(master);
    padGain = ctx.createGain(); padGain.gain.value = 0.15; padGain.connect(filter);
    padOsc = ctx.createOscillator(); padOsc.type = "sawtooth"; padOsc.frequency.value = 110;
    padOsc2 = ctx.createOscillator(); padOsc2.type = "sine"; padOsc2.frequency.value = 164.81;
    padOsc.connect(padGain); padOsc2.connect(padGain);
    bassGain = ctx.createGain(); bassGain.gain.value = 0; bassGain.connect(master);
    bassOsc = ctx.createOscillator(); bassOsc.type = "square"; bassOsc.frequency.value = 55; bassOsc.connect(bassGain);
    drumGain = ctx.createGain(); drumGain.gain.value = 0; drumGain.connect(master);
    padOsc.start(); padOsc2.start(); bassOsc.start();
    master.gain.linearRampToValueAtTime(0.6, ctx.currentTime + 1);
  };

  const kick = () => {
    if (!ctx) return;
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.setValueAtTime(140, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(drumGain.gain.value, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    o.connect(g); g.connect(master); o.start(); o.stop(ctx.currentTime + 0.16);
  };

  const update = (p) => {
    if (!ctx || !p) return;
    const t = ctx.currentTime; const intensity = p.intensity ?? 0.2;
    filter.frequency.linearRampToValueAtTime(400 + intensity * 4000, t + 0.4);
    bassGain.gain.linearRampToValueAtTime(p.layers?.includes("bass") ? 0.18 : 0, t + 0.4);
    drumGain.gain.value = p.layers?.includes("drums") ? 0.5 : 0;
    padGain.gain.linearRampToValueAtTime(0.12 + intensity * 0.1, t + 0.4);
    master.gain.linearRampToValueAtTime(p.master_volume ?? 0.6, t + 0.4);
    mixBus?.update(p.mix);
    clearInterval(drumInterval);
    if (p.layers?.includes("drums")) { const beat = 60 / (p.bpm || 120) * 1000; drumInterval = setInterval(kick, beat); }
  };

  const stop = () => {
    clearInterval(drumInterval);
    if (!ctx) return;
    try { master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3); setTimeout(() => ctx.close(), 400); } catch { /* noop */ }
  };

  return { start, update, stop };
}
